/**
 * OWASP Top 10:2025 Data
 * Source: https://owasp.org/Top10/2025/
 * 
 * This file contains all vulnerability data including:
 * - Dossier information for briefings
 * - Hunt phase threats and decoys
 * - Boss battle attacks and remediations
 */

export const owaspTop10 = [
  // ===========================================================================
  // A10: Mishandling of Exceptional Conditions (Level 1 - NEW in 2025)
  // ===========================================================================
  {
    id: "A10",
    rank: 10,
    name: "Mishandling of Exceptional Conditions",
    shortName: "EXCEPTION",
    color: "#7CFC00", // Lawn green - easiest
    bounty: 1000,
    dangerLevel: 1,
    
    dossier: {
      aliases: [
        "Uncaught Exception Handler",
        "Error Disclosure Agent",
        "Fail-Open Specialist"
      ],
      description: "A cunning adversary that exploits how applications handle unexpected situations. When errors occur, this threat causes systems to fail open, expose sensitive information in stack traces, or crash entirely - leaving doors wide open.",
      modesOperandi: [
        "Triggering unhandled exceptions to crash services",
        "Extracting sensitive data from verbose error messages",
        "Exploiting fail-open conditions for unauthorized access",
        "Causing denial of service through resource exhaustion"
      ],
      evidenceAtCrimeScenes: [
        "catch(e) { /* empty */ }",
        "throw new Error(userInput)",
        "console.log(error.stack)",
        "if (error) return true; // fail open"
      ],
      fortifyFlags: [
        "Empty_Catch_Block",
        "System_Information_Leak",
        "Denial_of_Service",
        "Poor_Error_Handling"
      ],
      coverityCheckers: [
        "RESOURCE_LEAK",
        "NULL_RETURNS",
        "CHECKED_RETURN"
      ],
      realWorldVictim: {
        name: "Multiple Cloud Services (2023)",
        description: "Improper exception handling in cloud APIs led to cascading failures and data exposure during outages."
      }
    },
    
    hunt: {
      threats: [
        { code: "catch(e) {}", label: "EMPTY CATCH", points: 100 },
        { code: "catch { return true }", label: "FAIL OPEN", points: 150 },
        { code: "res.send(err.stack)", label: "STACK LEAK", points: 125 },
        { code: "throw userInput", label: "USER THROW", points: 175 },
        { code: "// TODO: handle error", label: "NO HANDLER", points: 100 }
      ],
      decoys: [
        { code: "catch(e) { log(e); throw }", label: "LOG+RETHROW", reason: "Properly logs and re-throws" },
        { code: "finally { cleanup() }", label: "FINALLY", reason: "Cleanup block is good practice" },
        { code: "catch(AppError)", label: "TYPED CATCH", reason: "Specific exception handling" },
        { code: "try-with-resources", label: "AUTO CLOSE", reason: "Ensures resource cleanup" }
      ]
    },
    
    boss: {
      ascii: `
    ╔═══════════════════════╗
    ║   ┌─────────────┐     ║
    ║   │  ! ERROR !  │     ║
    ║   │  █████████  │     ║
    ║   │  █ ╳   ╳ █  │     ║
    ║   │  █   ▽   █  │     ║
    ║   │  █ ~~~~~ █  │     ║
    ║   │  █████████  │     ║
    ║   └─────────────┘     ║
    ║     EXCEPTION         ║
    ╚═══════════════════════╝`,
      taunt: "I WILL CRASH YOUR SYSTEM!",
      attacks: [
        {
          code: "catch(e) { }",
          correct: 0,
          options: [
            "Log error and fail safely",
            "Return null",
            "Ignore and continue"
          ],
          explanation: "Empty catch blocks hide errors. Always log and handle appropriately."
        },
        {
          code: "if(error) return true // allow access",
          correct: 1,
          options: [
            "Add more conditions",
            "Fail closed - deny on error",
            "Log the error first"
          ],
          explanation: "Fail-open conditions grant access when they shouldn't. Always fail closed."
        },
        {
          code: "res.json({ error: err.stack })",
          correct: 2,
          options: [
            "Pretty print the stack",
            "Add error code",
            "Return generic message to user"
          ],
          explanation: "Stack traces reveal internal paths, versions, and code structure to attackers."
        }
      ]
    }
  },

  // ===========================================================================
  // A09: Security Logging and Alerting Failures (Level 2)
  // ===========================================================================
  {
    id: "A09",
    rank: 9,
    name: "Security Logging and Alerting Failures",
    shortName: "LOG FAIL",
    color: "#00FF7F", // Spring green
    bounty: 1200,
    dangerLevel: 2,
    
    dossier: {
      aliases: [
        "The Invisible Intruder",
        "Audit Trail Eraser",
        "Silent Breach Artist"
      ],
      description: "This threat operates in the shadows, exploiting applications that don't properly log security events. Without adequate logging, breaches go undetected for months. Without alerting, no one knows until it's too late.",
      modesOperandi: [
        "Conducting attacks that leave no audit trail",
        "Exploiting systems with no intrusion detection",
        "Operating during logging blackouts",
        "Injecting false log entries to hide activity"
      ],
      evidenceAtCrimeScenes: [
        "// logging disabled in prod",
        "log.info(password)",
        "log.debug(request) // all requests",
        "catch(e) { /* no logging */ }"
      ],
      fortifyFlags: [
        "Log_Forging",
        "Privacy_Violation",
        "Missing_Security_Log",
        "Insufficient_Logging"
      ],
      coverityCheckers: [
        "AUDIT_SINK",
        "SENSITIVE_DATA_LOG"
      ],
      realWorldVictim: {
        name: "Equifax (2017)",
        description: "Attackers had access for 76 days before detection due to inadequate logging and monitoring."
      }
    },
    
    hunt: {
      threats: [
        { code: "log.info(user.password)", label: "PWD LOG", points: 150 },
        { code: "// logging off in prod", label: "NO LOGS", points: 125 },
        { code: "log(userInput) // unsanitized", label: "LOG INJECT", points: 175 },
        { code: "catch { /* silent */ }", label: "SILENT FAIL", points: 125 },
        { code: "log.debug(creditCard)", label: "PII LOG", points: 150 }
      ],
      decoys: [
        { code: "log.info('Login attempt')", label: "AUTH LOG", reason: "Logging auth events is good" },
        { code: "log.warn('Rate limited')", label: "RATE LOG", reason: "Security event logging" },
        { code: "audit.log(userId, action)", label: "AUDIT", reason: "Proper audit trail" },
        { code: "log.error(e.message)", label: "ERR MSG", reason: "Logging message, not sensitive data" }
      ]
    },
    
    boss: {
      ascii: `
    ╔═══════════════════════╗
    ║   ┌─────────────┐     ║
    ║   │  ░░░░░░░░░  │     ║
    ║   │  ░ ?   ? ░  │     ║
    ║   │  ░       ░  │     ║
    ║   │  ░ ????? ░  │     ║
    ║   │  ░░░░░░░░░  │     ║
    ║   └─────────────┘     ║
    ║     LOG FAIL          ║
    ╚═══════════════════════╝`,
      taunt: "YOU'LL NEVER SEE ME COMING!",
      attacks: [
        {
          code: "log.debug(request.body)",
          correct: 1,
          options: [
            "Log to separate file",
            "Sanitize and mask sensitive fields",
            "Disable debug logging"
          ],
          explanation: "Request bodies may contain passwords, tokens, PII. Always sanitize before logging."
        },
        {
          code: "// TODO: add security logging",
          correct: 0,
          options: [
            "Implement comprehensive audit logging",
            "Add console.log statements",
            "Log to browser console"
          ],
          explanation: "Security events (auth, access, changes) must be logged to detect breaches."
        },
        {
          code: "log.info(userInput)",
          correct: 2,
          options: [
            "Encode the input",
            "Truncate long inputs",
            "Validate and sanitize to prevent log injection"
          ],
          explanation: "Unsanitized input in logs enables log injection/forging attacks."
        }
      ]
    }
  },

  // ===========================================================================
  // A08: Software or Data Integrity Failures (Level 3)
  // ===========================================================================
  {
    id: "A08",
    rank: 8,
    name: "Software or Data Integrity Failures",
    shortName: "INTEGRITY",
    color: "#00CED1", // Dark turquoise
    bounty: 1400,
    dangerLevel: 3,
    
    dossier: {
      aliases: [
        "The Tamperer",
        "Unsigned Payload Smuggler",
        "Deserialization Demon"
      ],
      description: "This adversary exploits systems that don't verify the integrity of code or data. Whether it's unsigned updates, tampered cookies, or malicious serialized objects - if you don't verify it, they will abuse it.",
      modesOperandi: [
        "Injecting malicious code via unsigned updates",
        "Tampering with serialized session data",
        "Exploiting insecure deserialization for RCE",
        "Modifying CI/CD pipelines to inject backdoors"
      ],
      evidenceAtCrimeScenes: [
        "JSON.parse(untrustedData)",
        "pickle.loads(userData)",
        "ObjectInputStream(userInput)",
        "eval(deserialize(cookie))"
      ],
      fortifyFlags: [
        "Unsafe_Deserialization",
        "Trust_Boundary_Violation",
        "Object_Injection",
        "Missing_Integrity_Check"
      ],
      coverityCheckers: [
        "UNSAFE_DESERIALIZATION",
        "TAINTED_SCALAR"
      ],
      realWorldVictim: {
        name: "SolarWinds (2020)",
        description: "Attackers compromised the build pipeline, injecting malware into signed updates affecting 18,000+ organizations."
      }
    },
    
    hunt: {
      threats: [
        { code: "pickle.loads(userInput)", label: "PICKLE", points: 200 },
        { code: "eval(atob(cookie))", label: "COOKIE EVAL", points: 175 },
        { code: "ObjectInputStream(req)", label: "DESERIAL", points: 200 },
        { code: "yaml.load(data)", label: "YAML UNSAFE", points: 175 },
        { code: "update without checksum", label: "NO VERIFY", points: 150 }
      ],
      decoys: [
        { code: "JSON.parse(apiResponse)", label: "JSON PARSE", reason: "JSON.parse is safe (no code execution)" },
        { code: "yaml.safe_load(data)", label: "SAFE YAML", reason: "safe_load prevents code execution" },
        { code: "verify(signature, data)", label: "SIG CHECK", reason: "Integrity verification" },
        { code: "checksum === expected", label: "CHECKSUM", reason: "Data integrity check" }
      ]
    },
    
    boss: {
      ascii: `
    ╔═══════════════════════╗
    ║   ┌─────────────┐     ║
    ║   │ {TAMPERED}  │     ║
    ║   │  █████████  │     ║
    ║   │  █ ◉   ◉ █  │     ║
    ║   │  █  ~~~  █  │     ║
    ║   │  █ <===> █  │     ║
    ║   │  █████████  │     ║
    ║   └─────────────┘     ║
    ║     INTEGRITY         ║
    ╚═══════════════════════╝`,
      taunt: "YOUR DATA IS MINE TO RESHAPE!",
      attacks: [
        {
          code: "ObjectInputStream(socket.getInputStream())",
          correct: 1,
          options: [
            "Add try-catch block",
            "Use allowlist for deserializable classes",
            "Close stream after use"
          ],
          explanation: "Unrestricted deserialization allows arbitrary object instantiation and RCE."
        },
        {
          code: "auto-update without signature verification",
          correct: 0,
          options: [
            "Cryptographically sign and verify all updates",
            "Use HTTPS for downloads",
            "Check file size"
          ],
          explanation: "Without signature verification, attackers can serve malicious updates."
        },
        {
          code: "cookie = base64(JSON.stringify(user))",
          correct: 2,
          options: [
            "Use stronger encoding",
            "Compress the data",
            "Sign cookies with HMAC"
          ],
          explanation: "Unsigned cookies can be tampered with. Always sign sensitive data."
        }
      ]
    }
  },

  // ===========================================================================
  // A07: Authentication Failures (Level 4)
  // ===========================================================================
  {
    id: "A07",
    rank: 7,
    name: "Authentication Failures",
    shortName: "AUTH FAIL",
    color: "#FFD700", // Gold
    bounty: 1600,
    dangerLevel: 4,
    
    dossier: {
      aliases: [
        "The Identity Thief",
        "Credential Stuffer",
        "Session Hijacker"
      ],
      description: "This threat exploits weaknesses in authentication mechanisms. Weak passwords, missing MFA, session fixation, credential stuffing - any flaw in 'who are you?' is an opportunity.",
      modesOperandi: [
        "Credential stuffing with leaked password databases",
        "Brute forcing weak password policies",
        "Session fixation and hijacking",
        "Exploiting password reset flaws"
      ],
      evidenceAtCrimeScenes: [
        "if (password === 'admin')",
        "session.id = req.params.sid",
        "token = md5(username)",
        "// no rate limiting on login"
      ],
      fortifyFlags: [
        "Hardcoded_Password",
        "Weak_Cryptographic_Hash",
        "Session_Fixation",
        "Brute_Force"
      ],
      coverityCheckers: [
        "HARDCODED_CREDENTIALS",
        "WEAK_PASSWORD_HASH"
      ],
      realWorldVictim: {
        name: "Colonial Pipeline (2021)",
        description: "Attackers used a compromised password from a legacy VPN account without MFA."
      }
    },
    
    hunt: {
      threats: [
        { code: "password === 'admin123'", label: "HARDCODE", points: 175 },
        { code: "token = md5(userId)", label: "WEAK TOKEN", points: 150 },
        { code: "// no login rate limit", label: "NO LIMIT", points: 125 },
        { code: "session = params.session", label: "FIXATION", points: 175 },
        { code: "rememberMe = password", label: "STORED PWD", points: 200 }
      ],
      decoys: [
        { code: "bcrypt.hash(password, 12)", label: "BCRYPT", reason: "Strong password hashing" },
        { code: "requireMFA(user)", label: "MFA", reason: "Multi-factor authentication" },
        { code: "session.regenerate()", label: "REGEN", reason: "Prevents session fixation" },
        { code: "rateLimit(login, 5/min)", label: "RATE LIM", reason: "Brute force protection" }
      ]
    },
    
    boss: {
      ascii: `
    ╔═══════════════════════╗
    ║   ┌─────────────┐     ║
    ║   │  [LOCKED]   │     ║
    ║   │  █████████  │     ║
    ║   │  █ ⊙   ⊙ █  │     ║
    ║   │  █    ◆  █  │     ║
    ║   │  █  ───  █  │     ║
    ║   │  █████████  │     ║
    ║   └─────────────┘     ║
    ║     AUTH FAIL         ║
    ╚═══════════════════════╝`,
      taunt: "YOUR IDENTITY IS MINE!",
      attacks: [
        {
          code: "function login(user, pass) { /* no rate limit */ }",
          correct: 0,
          options: [
            "Implement progressive delays and lockout",
            "Add CAPTCHA",
            "Log failed attempts"
          ],
          explanation: "Without rate limiting, attackers can brute force credentials endlessly."
        },
        {
          code: "token = SHA1(username + timestamp)",
          correct: 1,
          options: [
            "Use SHA256 instead",
            "Use cryptographically secure random tokens",
            "Add salt to the hash"
          ],
          explanation: "Predictable tokens can be guessed. Use CSPRNG for all security tokens."
        },
        {
          code: "password.length >= 6",
          correct: 2,
          options: [
            "Require 8+ characters",
            "Add special character requirement",
            "Check against breached password lists"
          ],
          explanation: "Short passwords are easily brute forced. Check against known breached passwords."
        }
      ]
    }
  },

  // ===========================================================================
  // A06: Insecure Design (Level 5)
  // ===========================================================================
  {
    id: "A06",
    rank: 6,
    name: "Insecure Design",
    shortName: "DESIGN",
    color: "#FFA500", // Orange
    bounty: 1800,
    dangerLevel: 5,
    
    dossier: {
      aliases: [
        "The Architect's Nightmare",
        "Threat Model Evader",
        "Business Logic Breaker"
      ],
      description: "This threat doesn't exploit bugs - it exploits missing security controls that were never designed in the first place. You can't patch your way out of a flawed architecture.",
      modesOperandi: [
        "Exploiting business logic flaws",
        "Abusing features that lack abuse controls",
        "Bypassing security through design gaps",
        "Chaining multiple low-risk issues into high-risk attacks"
      ],
      evidenceAtCrimeScenes: [
        "// TODO: add authentication",
        "// security review: later",
        "// threat model: none",
        "function transferFunds() { /* no limits */ }"
      ],
      fortifyFlags: [
        "Missing_Authentication",
        "Missing_Authorization",
        "Business_Logic_Error",
        "Race_Condition"
      ],
      coverityCheckers: [
        "MISSING_LOCK",
        "RACE_CONDITION"
      ],
      realWorldVictim: {
        name: "Uber (2016)",
        description: "Insecure design allowed drivers to see rider personal data, and a hardcoded key in source code led to breach of 57M records."
      }
    },
    
    hunt: {
      threats: [
        { code: "// TODO: add auth check", label: "NO AUTH", points: 175 },
        { code: "transfer(any, any, any)", label: "NO LIMITS", points: 200 },
        { code: "// threat model: N/A", label: "NO MODEL", points: 150 },
        { code: "if (user.isAdmin) // client", label: "CLIENT CHK", points: 175 },
        { code: "verify on frontend only", label: "FE ONLY", points: 175 }
      ],
      decoys: [
        { code: "securityReview(design)", label: "SEC REV", reason: "Security review process" },
        { code: "threatModel.analyze()", label: "THREAT MOD", reason: "Threat modeling" },
        { code: "rateLimit(api)", label: "RATE LIM", reason: "Abuse prevention" },
        { code: "@PreAuthorize", label: "AUTHZ", reason: "Authorization annotation" }
      ]
    },
    
    boss: {
      ascii: `
    ╔═══════════════════════╗
    ║   ┌─────────────┐     ║
    ║   │  BLUEPRINT  │     ║
    ║   │  █████████  │     ║
    ║   │  █ ◇   ◇ █  │     ║
    ║   │  █   ╱╲  █  │     ║
    ║   │  █  ════ █  │     ║
    ║   │  █████████  │     ║
    ║   └─────────────┘     ║
    ║     DESIGN            ║
    ╚═══════════════════════╝`,
      taunt: "YOU CAN'T PATCH WHAT WAS NEVER BUILT!",
      attacks: [
        {
          code: "No rate limit on password reset emails",
          correct: 1,
          options: [
            "Add CAPTCHA after 3 attempts",
            "Limit to 3 requests per hour per account",
            "Require current password"
          ],
          explanation: "Unlimited password resets enable account enumeration and spam attacks."
        },
        {
          code: "User role checked only in UI, not API",
          correct: 0,
          options: [
            "Enforce authorization on every API endpoint",
            "Obfuscate the API",
            "Add API key"
          ],
          explanation: "Client-side checks are easily bypassed. Always enforce on the server."
        },
        {
          code: "No threat modeling performed",
          correct: 2,
          options: [
            "Add more tests",
            "Hire pen testers",
            "Integrate threat modeling in design phase"
          ],
          explanation: "Security must be designed in, not bolted on. Threat model early and often."
        }
      ]
    }
  },

  // ===========================================================================
  // A05: Injection (Level 6)
  // ===========================================================================
  {
    id: "A05",
    rank: 5,
    name: "Injection",
    shortName: "INJECT",
    color: "#FF6347", // Tomato
    bounty: 2000,
    dangerLevel: 6,
    
    dossier: {
      aliases: [
        "SQL Slammer",
        "Command Crusher",
        "Query Manipulator"
      ],
      description: "The classic threat that refuses to die. When user input is concatenated into queries or commands without sanitization, this adversary takes control. SQL, NoSQL, OS commands, LDAP - nothing is safe.",
      modesOperandi: [
        "SQL injection to dump databases",
        "Command injection for system access",
        "LDAP injection to bypass authentication",
        "NoSQL injection in MongoDB queries"
      ],
      evidenceAtCrimeScenes: [
        "query = \"SELECT * FROM users WHERE id=\" + id",
        "exec(\"rm \" + filename)",
        "db.find({user: req.body.user})",
        "ldap.search(\"(uid=\" + username + \")\")"
      ],
      fortifyFlags: [
        "SQL_Injection",
        "Command_Injection",
        "LDAP_Injection",
        "NoSQL_Injection",
        "XPath_Injection"
      ],
      coverityCheckers: [
        "SQL_INJECTION",
        "OS_CMD_INJECTION",
        "TAINTED_STRING"
      ],
      realWorldVictim: {
        name: "Heartland Payment Systems (2008)",
        description: "SQL injection led to theft of 130 million credit card numbers - largest breach at that time."
      }
    },
    
    hunt: {
      threats: [
        { code: "\"SELECT * FROM \" + table", label: "SQL CAT", points: 200 },
        { code: "exec(\"ls \" + dir)", label: "CMD INJ", points: 225 },
        { code: "eval(userCode)", label: "EVAL", points: 250 },
        { code: "db.query({$where: input})", label: "NOSQL", points: 200 },
        { code: "xpath.query(\"/\" + path)", label: "XPATH", points: 175 }
      ],
      decoys: [
        { code: "stmt.setString(1, id)", label: "PREPARED", reason: "Parameterized query" },
        { code: "db.query({user: sanitize(u)})", label: "SANITIZE", reason: "Input sanitization" },
        { code: "allowedCommands.includes(cmd)", label: "ALLOWLIST", reason: "Command allowlisting" },
        { code: "escapeHtml(input)", label: "ESCAPE", reason: "Output encoding" }
      ]
    },
    
    boss: {
      ascii: `
    ╔═══════════════════════╗
    ║   ┌─────────────┐     ║
    ║   │  '; DROP--  │     ║
    ║   │  █████████  │     ║
    ║   │  █ ●   ● █  │     ║
    ║   │  █   <>  █  │     ║
    ║   │  █ \\──// █  │     ║
    ║   │  █████████  │     ║
    ║   └─────────────┘     ║
    ║     INJECTION         ║
    ╚═══════════════════════╝`,
      taunt: "YOUR QUERIES BELONG TO ME!",
      attacks: [
        {
          code: "\"SELECT * FROM users WHERE name='\" + name + \"'\"",
          correct: 0,
          options: [
            "Use parameterized queries / prepared statements",
            "Escape single quotes",
            "Validate input length"
          ],
          explanation: "String concatenation in SQL enables injection. Always use parameterized queries."
        },
        {
          code: "child_process.exec('convert ' + filename)",
          correct: 1,
          options: [
            "Use execSync instead",
            "Use execFile with argument array",
            "Add sudo"
          ],
          explanation: "exec() with string concatenation allows command injection. Use execFile with arrays."
        },
        {
          code: "eval(req.body.calculation)",
          correct: 2,
          options: [
            "Wrap in try-catch",
            "Run in sandbox",
            "Never eval user input - use safe parser"
          ],
          explanation: "eval() executes arbitrary code. Never use with user input. Use safe math parsers."
        }
      ]
    }
  },

  // ===========================================================================
  // A04: Cryptographic Failures (Level 7)
  // ===========================================================================
  {
    id: "A04",
    rank: 4,
    name: "Cryptographic Failures",
    shortName: "CRYPTO",
    color: "#FF4500", // Orange red
    bounty: 2200,
    dangerLevel: 7,
    
    dossier: {
      aliases: [
        "The Decryptor",
        "Hash Cracker",
        "Key Exposer"
      ],
      description: "This threat exploits weak or missing cryptography. MD5 passwords, unencrypted data, hardcoded keys, HTTP instead of HTTPS - any cryptographic weakness is an opportunity to expose your secrets.",
      modesOperandi: [
        "Cracking weak password hashes",
        "Intercepting unencrypted traffic",
        "Exploiting hardcoded encryption keys",
        "Downgrade attacks on TLS"
      ],
      evidenceAtCrimeScenes: [
        "md5(password)",
        "const SECRET = 'hardcoded123'",
        "http:// in production",
        "cipher = DES.new(key)"
      ],
      fortifyFlags: [
        "Weak_Cryptographic_Hash",
        "Hardcoded_Encryption_Key",
        "Insecure_Transport",
        "Weak_Encryption",
        "Privacy_Violation"
      ],
      coverityCheckers: [
        "WEAK_CRYPTO",
        "HARDCODED_CREDENTIALS",
        "RISKY_CRYPTO"
      ],
      realWorldVictim: {
        name: "Adobe (2013)",
        description: "153 million passwords stored with weak encryption (3DES-ECB) and password hints in plaintext."
      }
    },
    
    hunt: {
      threats: [
        { code: "md5(password)", label: "MD5 PWD", points: 200 },
        { code: "SECRET_KEY = 'abc123'", label: "HARD KEY", points: 225 },
        { code: "DES.encrypt(data)", label: "WEAK DES", points: 175 },
        { code: "http://api.internal", label: "NO TLS", points: 150 },
        { code: "AES-ECB mode", label: "ECB MODE", points: 200 }
      ],
      decoys: [
        { code: "bcrypt.hash(pw, 12)", label: "BCRYPT", reason: "Strong password hash" },
        { code: "env.SECRET_KEY", label: "ENV VAR", reason: "Key from environment" },
        { code: "AES-256-GCM", label: "AES GCM", reason: "Authenticated encryption" },
        { code: "https:// everywhere", label: "HTTPS", reason: "TLS encryption" }
      ]
    },
    
    boss: {
      ascii: `
    ╔═══════════════════════╗
    ║   ┌─────────────┐     ║
    ║   │  🔓 CRACKED │     ║
    ║   │  █████████  │     ║
    ║   │  █ ⊗   ⊗ █  │     ║
    ║   │  █  \\█/  █  │     ║
    ║   │  █ ═════ █  │     ║
    ║   │  █████████  │     ║
    ║   └─────────────┘     ║
    ║     CRYPTO FAIL       ║
    ╚═══════════════════════╝`,
      taunt: "YOUR SECRETS ARE ALREADY MINE!",
      attacks: [
        {
          code: "hash = MD5(password)",
          correct: 0,
          options: [
            "Use bcrypt/scrypt/Argon2 with salt",
            "Use SHA-256 instead",
            "Add pepper to the hash"
          ],
          explanation: "MD5 is broken for passwords. Use bcrypt, scrypt, or Argon2 with work factor."
        },
        {
          code: "const API_KEY = 'sk_live_abc123'",
          correct: 1,
          options: [
            "Encode in base64",
            "Load from environment variables or vault",
            "Move to config file"
          ],
          explanation: "Hardcoded secrets in code end up in repos. Use environment vars or secret managers."
        },
        {
          code: "cipher = AES.new(key, AES.MODE_ECB)",
          correct: 2,
          options: [
            "Use longer key",
            "Add initialization vector",
            "Use GCM or CBC mode, never ECB"
          ],
          explanation: "ECB mode reveals patterns in encrypted data. Use GCM for authenticated encryption."
        }
      ]
    }
  },

  // ===========================================================================
  // A03: Software Supply Chain Failures (Level 8 - NEW in 2025)
  // ===========================================================================
  {
    id: "A03",
    rank: 3,
    name: "Software Supply Chain Failures",
    shortName: "SUPPLY",
    color: "#DC143C", // Crimson
    bounty: 2500,
    dangerLevel: 8,
    
    dossier: {
      aliases: [
        "Dependency Demon",
        "Package Poisoner",
        "Pipeline Infiltrator"
      ],
      description: "NEW in 2025. This threat exploits the software supply chain - vulnerable dependencies, compromised packages, malicious updates. When 80% of your code comes from dependencies, that's 80% attack surface.",
      modesOperandi: [
        "Publishing malicious packages with typosquatting",
        "Compromising legitimate package maintainers",
        "Injecting malware into CI/CD pipelines",
        "Exploiting known vulnerabilities in dependencies"
      ],
      evidenceAtCrimeScenes: [
        "lodash@4.17.20 // vulnerable",
        "npm install --ignore-scripts",
        "pip install -r requirements.txt // no hash",
        "FROM ubuntu:latest // mutable tag"
      ],
      fortifyFlags: [
        "Vulnerable_Third_Party_Component",
        "Unvetted_Dependency",
        "Missing_Integrity_Check",
        "Outdated_Library"
      ],
      coverityCheckers: [
        "VULNERABLE_COMPONENT",
        "UNTRUSTED_SOURCE"
      ],
      realWorldVictim: {
        name: "xz Utils Backdoor (2024)",
        description: "Sophisticated multi-year social engineering attack injected backdoor into critical Linux compression library."
      }
    },
    
    hunt: {
      threats: [
        { code: "log4j@2.14.0", label: "LOG4J", points: 250 },
        { code: "lodash-utils (typosquat)", label: "TYPOSQT", points: 225 },
        { code: "FROM node:latest", label: "MUTABLE", points: 175 },
        { code: "npm audit --ignore", label: "IGNORE", points: 200 },
        { code: "curl | bash", label: "CURL BASH", points: 250 }
      ],
      decoys: [
        { code: "npm audit fix", label: "AUDIT FIX", reason: "Fixing vulnerabilities" },
        { code: "package-lock.json", label: "LOCKFILE", reason: "Dependency pinning" },
        { code: "FROM node:18.19.0@sha256", label: "PINNED", reason: "Immutable image reference" },
        { code: "dependabot enabled", label: "DEPBOT", reason: "Automated updates" }
      ]
    },
    
    boss: {
      ascii: `
    ╔═══════════════════════╗
    ║   ┌─────────────┐     ║
    ║   │ ⚠ PACKAGE ⚠ │     ║
    ║   │  █████████  │     ║
    ║   │  █ ◈   ◈ █  │     ║
    ║   │  █ \\═══/ █  │     ║
    ║   │  █ │pkg│ █  │     ║
    ║   │  █████████  │     ║
    ║   └─────────────┘     ║
    ║     SUPPLY CHAIN      ║
    ╚═══════════════════════╝`,
      taunt: "I AM IN YOUR DEPENDENCIES!",
      attacks: [
        {
          code: "npm install random-package-from-tutorial",
          correct: 1,
          options: [
            "Install with --save-dev",
            "Verify package authenticity and check for typosquatting",
            "Use yarn instead"
          ],
          explanation: "Always verify packages are legitimate. Check download counts, maintainers, and spelling."
        },
        {
          code: "FROM ubuntu:latest",
          correct: 0,
          options: [
            "Pin to specific version with SHA digest",
            "Use alpine instead",
            "Add HEALTHCHECK"
          ],
          explanation: "Mutable tags can change. Pin images with SHA256 digest for reproducible builds."
        },
        {
          code: "dependencies: { '*': 'latest' }",
          correct: 2,
          options: [
            "Use semver ranges",
            "Update weekly",
            "Pin exact versions with lockfile"
          ],
          explanation: "Always pin dependencies. Use lockfiles and verify integrity hashes."
        }
      ]
    }
  },

  // ===========================================================================
  // A02: Security Misconfiguration (Level 9)
  // ===========================================================================
  {
    id: "A02",
    rank: 2,
    name: "Security Misconfiguration",
    shortName: "MISCONFIG",
    color: "#B22222", // Firebrick
    bounty: 2800,
    dangerLevel: 9,
    
    dossier: {
      aliases: [
        "The Default Exploiter",
        "Config Chaos Agent",
        "Debug Mode Hunter"
      ],
      description: "Moved to #2 in 2025. This threat exploits misconfigured security settings - default credentials, exposed admin panels, verbose errors, unnecessary features enabled. The most common vulnerability in cloud environments.",
      modesOperandi: [
        "Exploiting default credentials (admin:admin)",
        "Accessing exposed debug endpoints",
        "Enumerating S3 buckets with public access",
        "Exploiting unnecessary enabled features"
      ],
      evidenceAtCrimeScenes: [
        "DEBUG = True in production",
        "admin:admin credentials",
        "S3 bucket: public-read",
        "X-Powered-By: Express 4.0.0"
      ],
      fortifyFlags: [
        "System_Information_Leak",
        "Hardcoded_Credentials",
        "Privacy_Violation",
        "Missing_Security_Headers"
      ],
      coverityCheckers: [
        "CONFIG_SENSITIVE_DATA",
        "HARDCODED_CREDENTIALS"
      ],
      realWorldVictim: {
        name: "Capital One (2019)",
        description: "Misconfigured WAF allowed SSRF attack to access AWS metadata and steal 100M customer records."
      }
    },
    
    hunt: {
      threats: [
        { code: "DEBUG = True # prod", label: "DEBUG ON", points: 200 },
        { code: "password: 'admin'", label: "DEFAULT", points: 225 },
        { code: "ACL: public-read", label: "PUBLIC S3", points: 250 },
        { code: "CORS: '*'", label: "CORS ALL", points: 175 },
        { code: "Server: Apache 2.4.1", label: "BANNER", points: 150 }
      ],
      decoys: [
        { code: "DEBUG = False", label: "DEBUG OFF", reason: "Debug disabled in prod" },
        { code: "ACL: private", label: "PRIVATE", reason: "Proper access control" },
        { code: "CORS: allowedOrigins", label: "CORS OK", reason: "Specific origins only" },
        { code: "Server: (removed)", label: "NO BANNER", reason: "Server header removed" }
      ]
    },
    
    boss: {
      ascii: `
    ╔═══════════════════════╗
    ║   ┌─────────────┐     ║
    ║   │ ⚙ CONFIG ⚙ │     ║
    ║   │  █████████  │     ║
    ║   │  █ ⊘   ⊘ █  │     ║
    ║   │  █  ┌─┐  █  │     ║
    ║   │  █  │█│  █  │     ║
    ║   │  █████████  │     ║
    ║   └─────────────┘     ║
    ║     MISCONFIG         ║
    ╚═══════════════════════╝`,
      taunt: "YOUR DEFAULTS ARE MY ENTRANCE!",
      attacks: [
        {
          code: "app.use(express.static('/', { dotfiles: 'allow' }))",
          correct: 0,
          options: [
            "Set dotfiles: 'deny' to hide config files",
            "Move static files elsewhere",
            "Add authentication"
          ],
          explanation: "Exposing dotfiles reveals .env, .git, and other sensitive configuration."
        },
        {
          code: "Cloud storage bucket: AllUsers READ",
          correct: 1,
          options: [
            "Add encryption",
            "Restrict to specific IAM roles/accounts",
            "Enable versioning"
          ],
          explanation: "Public buckets expose data to anyone. Use IAM policies for access control."
        },
        {
          code: "res.header('X-Powered-By', 'Express')",
          correct: 2,
          options: [
            "Change to 'PHP'",
            "Add version number",
            "Remove or disable header entirely"
          ],
          explanation: "Technology disclosure helps attackers choose exploits. Remove unnecessary headers."
        }
      ]
    }
  },

  // ===========================================================================
  // A01: Broken Access Control (Level 10 - FINAL BOSS)
  // ===========================================================================
  {
    id: "A01",
    rank: 1,
    name: "Broken Access Control",
    shortName: "ACCESS",
    color: "#8B0000", // Dark red - hardest
    bounty: 3500,
    dangerLevel: 10,
    
    dossier: {
      aliases: [
        "The Access Breaker",
        "Privilege Escalator",
        "IDOR Master"
      ],
      description: "#1 since 2021 and still the champion. Found in 100% of applications tested. This threat bypasses all authorization - viewing others' data, performing admin actions, escalating privileges. The most dangerous vulnerability class.",
      modesOperandi: [
        "IDOR - changing /user/123 to /user/124",
        "Vertical privilege escalation to admin",
        "Horizontal access to other users' data",
        "Bypassing access controls via API"
      ],
      evidenceAtCrimeScenes: [
        "/api/user/{id} // no auth check",
        "if (req.query.admin === 'true')",
        "// TODO: verify ownership",
        "role = cookie.role // client-side"
      ],
      fortifyFlags: [
        "Access_Control_Violation",
        "Insecure_Direct_Object_Reference",
        "Missing_Authorization",
        "Privilege_Escalation"
      ],
      coverityCheckers: [
        "MISSING_AUTHORIZATION",
        "IDOR",
        "PRIVILEGE_ESCALATION"
      ],
      realWorldVictim: {
        name: "Facebook (2018)",
        description: "Access token vulnerability exposed 50M accounts. Attackers could access any account via IDOR in View As feature."
      }
    },
    
    hunt: {
      threats: [
        { code: "getUser(req.params.id)", label: "IDOR", points: 250 },
        { code: "if (params.isAdmin)", label: "PARAM AUTH", points: 275 },
        { code: "// verify owner later", label: "NO VERIFY", points: 225 },
        { code: "role = localStorage.role", label: "CLIENT ROLE", points: 275 },
        { code: "DELETE /users/* // open", label: "MASS DELETE", points: 300 }
      ],
      decoys: [
        { code: "verifyOwnership(user, id)", label: "VERIFY", reason: "Ownership verification" },
        { code: "@PreAuthorize('hasRole')", label: "AUTHZ", reason: "Authorization check" },
        { code: "if (jwt.userId === id)", label: "JWT CHECK", reason: "Token-based verification" },
        { code: "rbac.check(user, action)", label: "RBAC", reason: "Role-based access control" }
      ]
    },
    
    boss: {
      ascii: `
    ╔═══════════════════════════════╗
    ║   ┌─────────────────────┐     ║
    ║   │  ☠ FINAL BOSS ☠    │     ║
    ║   │  ███████████████████│     ║
    ║   │  ██ ◉         ◉ ██ │     ║
    ║   │  ██     ███     ██ │     ║
    ║   │  ██   ╔═════╗   ██ │     ║
    ║   │  ██   ║ADMIN║   ██ │     ║
    ║   │  ██   ╚═════╝   ██ │     ║
    ║   │  ███████████████████│     ║
    ║   └─────────────────────┘     ║
    ║    BROKEN ACCESS CONTROL      ║
    ╚═══════════════════════════════╝`,
      taunt: "I AM THE KING OF VULNERABILITIES!",
      phases: 3, // Final boss has multiple phases
      attacks: [
        // Phase 1: IDOR
        {
          phase: 1,
          phaseName: "IDOR Attack",
          code: "GET /api/documents/{id} - returns any document by ID",
          correct: 0,
          options: [
            "Verify requesting user owns the document",
            "Use random UUIDs instead of sequential IDs",
            "Add rate limiting"
          ],
          explanation: "Always verify the requesting user has permission to access the specific resource."
        },
        // Phase 2: Privilege Escalation
        {
          phase: 2,
          phaseName: "Vertical Escalation",
          code: "POST /api/users/promote { role: 'admin' }",
          correct: 1,
          options: [
            "Validate role is valid enum",
            "Check if requester can grant this role",
            "Log the request"
          ],
          explanation: "Users should never be able to elevate their own privileges. Verify authority to grant roles."
        },
        // Phase 3: API Bypass
        {
          phase: 3,
          phaseName: "Full Compromise",
          code: "UI hides admin button, but /admin/deleteAll exists",
          correct: 2,
          options: [
            "Obfuscate the endpoint",
            "Add CAPTCHA",
            "Enforce authorization on EVERY endpoint"
          ],
          explanation: "Security through obscurity fails. Every endpoint must verify authorization server-side."
        }
      ]
    }
  }
];

// Helper to get vulnerability by rank (level number)
export const getVulnByLevel = (level) => {
  return owaspTop10.find(v => v.rank === (11 - level)); // Level 1 = A10, Level 10 = A01
};

// Helper to get vulnerability by ID
export const getVulnById = (id) => {
  return owaspTop10.find(v => v.id === id);
};

// Export ranks for scoring
export const ranks = [
  { minScore: 0, name: "Security Intern", color: "#666666" },
  { minScore: 5000, name: "Junior Analyst", color: "#7CFC00" },
  { minScore: 10000, name: "Security Analyst", color: "#00CED1" },
  { minScore: 15000, name: "Senior Analyst", color: "#FFD700" },
  { minScore: 22000, name: "Security Engineer", color: "#FFA500" },
  { minScore: 30000, name: "Senior Engineer", color: "#FF6347" },
  { minScore: 40000, name: "Principal Architect", color: "#DC143C" },
  { minScore: 50000, name: "CISO", color: "#8B0000" }
];

export const getRank = (score) => {
  for (let i = ranks.length - 1; i >= 0; i--) {
    if (score >= ranks[i].minScore) {
      return ranks[i];
    }
  }
  return ranks[0];
};
